/* eslint-disable @next/next/no-css-tags */
import { cx } from "cva";
import Head from "next/head";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { BuildHtml } from "~/components/BuildHtml";
import {
  Body,
  Headline,
  HeadlineProps,
  Icon,
  IconButton,
  IconProps,
} from "~/ui-library";
import { XMarkIcon, MagnifyingGlassIcon } from "~/ui-library/icons";
import {
  EvervizAdweek,
  EvervizHollywood,
  EvervizMc,
  EvervizPolitico,
} from "~/ui-library/logos";
import { useFeatureFlags } from "~/lib/client/useFeatureFlags";
import { useEvervizScriptInitialize } from "~/components/EvervizChart/useEvervizScriptInitialize";
import { useDocument } from "~/lib/client/useDocument";
import { isPopulatedString } from "~/lib/shared/isPopulatedString";
import { Modal } from "~/components/Modal";
import { EvervizChartHeader } from "./EvervizChartHeader";
import { EvervizChartFooter } from "./EvervizChartFooter";

export interface EvervizChartProps {
  /** text that sits at top of the chart */
  headline?: string;
  /** semantic element to render the headline as - defaults to <h3> */
  headlineAs?: HeadlineProps["as"];
  /** headline level styles to apply to the headline - defaults to 3 */
  headlineLevel?: HeadlineProps["level"];
  /** id to be attached to the title element (for navigation purposes) */
  headlineId?: string;

  /** Text that sits below headline */
  subtitle?: string;
  /** text that sits below subtitle */
  title?: string;
  /** text that redners across from the logo */
  footnote?: string;
  logo?: string;
  injectId: string;
  chartId: string;
  /** Methodology that sits below the logo/footnote */
  methodology?: string;
  image?: string;
  altText?: string;

  /** Whether or not to show any text above the chart - defaults to true */
  showHeader?: boolean;
  /** Whether or not to show the headline - default to false */
  hideHeadline?: boolean;
  className?: string;

  /* Unused properties */
  evervizEmbedCode?: string;
  contentRef?: string;
}

const logoNameToIcon: Map<string, IconProps> = new Map([
  ["iconMorningConsult", { SvgIcon: EvervizMc, title: "Morning Consult Logo" }],
  ["iconMCPolitico", { SvgIcon: EvervizPolitico, title: "Politico Logo" }],
  [
    "iconMCHollywoodReporter",
    { SvgIcon: EvervizHollywood, title: "Hollywood Reporter Logo" },
  ],
  ["iconMCAdweek", { SvgIcon: EvervizAdweek, title: "Adweek Logo" }],
]);

export const EvervizChart = (evervizChartProps: EvervizChartProps) => {
  const {
    headline,
    headlineAs = "h3",
    headlineLevel = 3,
    subtitle,
    title,
    headlineId,
    footnote,
    logo,
    injectId,
    chartId,
    methodology,
    className,
    image,
    altText,
    showHeader = true,
    hideHeadline = false,
  } = evervizChartProps;

  const [isFullscreen, setIsFullscreen] = useState(false);

  const { enableExpandChart } = useFeatureFlags();

  const evervizContainerRef = useRef<HTMLDivElement | null>(null);

  const document = useDocument();
  const scriptAnchorRef = useRef<HTMLHeadElement | null>(
    typeof document !== "undefined" ? document?.querySelector("head") : null
  );

  /**
   * Determines whether or not to render the everviz script.
   * This is enabled on the first render in a useEffect.
   *
   * We cannot attach the native <script /> tag with the first render otherwise it will result in a hydration error,
   *
   * The everviz script must be ran in full to properly load,
   * otherwise the chart will never render on page revisits.
   */
  const { forceRetry } = useEvervizScriptInitialize({
    enabled: Boolean(injectId),
    evervizContainerRef,
    maxRetries: 3,
    retryTimeout: 1000,
    scriptAnchorRef,
    src: `https://app.everviz.com/inject/${injectId}/?v=5`,
  });

  const [isReady, setIsReady] = useState(false);

  const ChartIcon = logoNameToIcon.get(logo || "");

  const chartIconContainer = ChartIcon ? (
    <Icon
      SvgIcon={ChartIcon?.SvgIcon}
      title={ChartIcon.title}
      size="scale"
      className="h-s"
      display="block"
    />
  ) : null;

  const openFullscreen = () => {
    setIsFullscreen(true);
    forceRetry();
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
    forceRetry();
  };

  useEffect(() => {
    setIsReady(true);
  }, []);

  return (
    <>
      <Head>
        <link
          rel="stylesheet"
          type="text/css"
          href="/fontawesome/css/all.css"
        />
      </Head>
      {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
      <div title={headline} className={cx("EverViz", className)}>
        {/* 
          TODO: Once enableExpandChart has been removed, we can remove the older header and footer components.
        */}
        {enableExpandChart ? (
          <div className="flex w-full flex-col pt-s">
            <div className="flex items-start justify-between border-t-[1px] border-solid border-gray-300 pt-s">
              <div className="flex-1">
                {showHeader ? (
                  <EvervizChartHeader
                    headline={headline}
                    subtitle={subtitle}
                    title={title}
                    headlineId={headlineId}
                    headlineAs={headlineAs}
                    headlineLevel={headlineLevel}
                    hideHeadline={hideHeadline}
                  />
                ) : null}
              </div>
              {/* Conditionally (when we have a chart) render the button to open the chart in fullscreen */}
              {injectId ? (
                <div className="ml-s hidden md:block">
                  <IconButton
                    aria-label="Open chart in fullscreen"
                    iconClassName="fill-transparent"
                    SvgIcon={MagnifyingGlassIcon}
                    onClick={openFullscreen}
                    size="large"
                    variant="subtle"
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Old Header */}
        {showHeader && !enableExpandChart ? (
          <div className="flex w-full flex-col border-t-[1px] border-solid border-gray-300 pt-s">
            {isPopulatedString(headline) && !hideHeadline && (
              <>
                <Headline
                  as={headlineAs}
                  id={headlineId}
                  level={headlineLevel}
                  className="scroll-mt-xxl pb-xs"
                >
                  {headline}
                </Headline>
              </>
            )}
            {isPopulatedString(subtitle) && (
              <>
                <Body className="text-body-regular">
                  <BuildHtml allowStyles html={subtitle} />
                </Body>
              </>
            )}
            {isPopulatedString(title) && (
              <Body bold className="py-xs text-body-regular">
                <BuildHtml allowStyles html={title} />
              </Body>
            )}
          </div>
        ) : null}

        {injectId && !isFullscreen ? (
          <div
            id={chartId}
            className={cx(isReady ? "" : "min-h-[350px]", chartId)}
            ref={evervizContainerRef}
          />
        ) : null}

        {image ? (
          <div className="max-w-full">
            <Image
              alt={altText || ""}
              className={"!relative object-contain"}
              src={image!}
              quality={100}
              fill
            />
          </div>
        ) : null}

        {enableExpandChart ? (
          <EvervizChartFooter
            footnote={footnote}
            methodology={methodology}
            chartIconContainer={chartIconContainer}
          />
        ) : (
          // Old Footer
          <>
            <div className="mt-m flex flex-row flex-wrap-reverse place-items-end justify-between border-b-[1px] border-solid border-gray-300 pb-xs text-gray-700">
              <>
                <div className="max-w-[300px] flex-auto overflow-hidden">
                  {chartIconContainer}
                </div>
                {isPopulatedString(footnote) && (
                  <div className="flex w-1/2 flex-auto justify-end pb-xs text-right text-body-xsmall text-gray-700 md:pb-0">
                    <span>{footnote}</span>
                  </div>
                )}
              </>
            </div>
            {isPopulatedString(methodology) && (
              <div className="pt-xs text-body-xsmall text-gray-700">
                <BuildHtml html={methodology} />
              </div>
            )}
          </>
        )}
      </div>

      {isFullscreen && enableExpandChart ? (
        <Modal
          onOverlayClick={closeFullscreen}
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className="custom-scrollbar mx-5 max-h-[90vh] w-full overflow-y-auto rounded-lg bg-white shadow-3 md:max-w-7xl lg:mx-16"
          isOpen={isFullscreen}
        >
          <div className="absolute right-2 top-2 z-[10000]">
            <IconButton
              aria-label="Close fullscreen"
              SvgIcon={XMarkIcon}
              onClick={closeFullscreen}
              size="large"
              variant="subtle"
            />
          </div>
          <div className="mt-12 p-2 md:p-6">
            <div>
              <div className="flex-1">
                {showHeader ? (
                  <EvervizChartHeader
                    headline={headline}
                    subtitle={subtitle}
                    title={title}
                    headlineId={headlineId}
                    headlineAs={headlineAs}
                    headlineLevel={headlineLevel}
                    hideHeadline={hideHeadline}
                  />
                ) : null}
              </div>

              <div className="min-h-[400px] flex-1">
                <div
                  id={chartId}
                  className={cx("min-h-[400px]", chartId)}
                  ref={evervizContainerRef}
                />
              </div>

              <EvervizChartFooter
                footnote={footnote}
                methodology={methodology}
                chartIconContainer={chartIconContainer}
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};
